import { Injectable } from '@angular/core';
import { CreateSpaceEvent, EfficiencyTransport, ReplaceSpaceEvent } from './models/efficiencies.models';
import { AvailabilitySpaceClass } from './models/space/availability-space.class';
import { NonRentableSpaceClass } from './models/space/non-rentable-space.class';
import { TenantSpaceClass } from './models/space/tenant-space.class';

@Injectable({ providedIn: 'root' })
export class SpaceEventsService {
  generateCreateSpaceEvent(propertyId: number, spaceToCreate: EfficiencyTransport): CreateSpaceEvent {
    const {
      floor,
      suiteNumber,
      containerRecordFloors,
      createSpaceRequest$,
      remainingSpace,
      remainingSpaceUOM,
      entityName,
      originalSpace,
      originalSpaceUOM,
      ownerOccupied,
      usage,
    } = spaceToCreate;
    return {
      propertyId,
      usage,
      floor,
      suiteNumber,
      containerRecordFloors,
      entityName,
      createSpaceRequest$,
      remainingSpace,
      remainingSpaceUOM,
      originalSpace,
      originalSpaceUOM,
      ownerOccupied,
    };
  }

  generateReplaceSpaceEvent(propertyId: number, spaceToReplace: EfficiencyTransport): ReplaceSpaceEvent {
    const { space } = spaceToReplace;

    let spaceSpecificReplaceEvent: Partial<ReplaceSpaceEvent> = {};
    if (space instanceof TenantSpaceClass) {
      spaceSpecificReplaceEvent = this.generateTenantReplaceEvent(space, spaceToReplace);
    } else if (space instanceof AvailabilitySpaceClass) {
      spaceSpecificReplaceEvent = this.generateAvailabilityReplaceEvent(space);
    } else if (space instanceof NonRentableSpaceClass) {
      spaceSpecificReplaceEvent = this.generateNRSReplaceEvent(spaceToReplace);
    }

    const commonSpaceReplaceEvent = this.getCommonReplaceEventData(propertyId, spaceToReplace);
    return {
      ...commonSpaceReplaceEvent,
      ...spaceSpecificReplaceEvent,
    };
  }

  private getCommonReplaceEventData(propertyId: number, spaceToReplace: EfficiencyTransport): ReplaceSpaceEvent {
    const { space, floor, containerRecordFloors, conversionType, createSpaceRequest$, closeSpaceRequest$ } =
      spaceToReplace;
    const { suiteNumber } = space;

    return {
      propertyId,
      conversionType,
      floor,
      containerRecordFloors,
      suiteNumber,
      createSpaceRequest$, // this attribute can be undefined and it's usually used for NRS related logic
      closeSpaceRequest$, // this attribute can be undefined and it's usually used for NRS related logic
      originalSpace: space.getAreaSpace(),
      originalSpaceUOM: space.getAreaSpaceUOM(),
    };
  }

  private generateTenantReplaceEvent(
    space: TenantSpaceClass,
    spaceToReplace: EfficiencyTransport
  ): Partial<ReplaceSpaceEvent> {
    // remainingSpace, remainingSpaceUOM are from NRS to Availability which is already available
    const { tenant, lease, companyName } = space;
    const { remainingSpace, remainingSpaceUOM } = spaceToReplace;
    return {
      leaseId: lease?.id,
      tenantId: tenant?.id,
      usage: tenant?.ref_property_usage_type_desc,
      currentCompanyName: companyName,
      remainingSpace,
      remainingSpaceUOM,
    };
  }

  private generateAvailabilityReplaceEvent(space: AvailabilitySpaceClass): Partial<ReplaceSpaceEvent> {
    const { id, refPropertyUsageTypeDesc } = space;
    return { availabilityId: id, usage: refPropertyUsageTypeDesc };
  }

  private generateNRSReplaceEvent(spaceToReplace: EfficiencyTransport): Partial<ReplaceSpaceEvent> {
    // remainingSpace, remainingSpaceUOM are from NRS which is already calculated
    const { remainingSpace, remainingSpaceUOM } = spaceToReplace;
    return {
      remainingSpace,
      remainingSpaceUOM,
    };
  }
}
